import * as React from "react";
import { Prompt, RouteComponentProps, withRouter } from "react-router-dom";

import { useStoreContext } from "@context";

import { Button, FileUploadModal, Icon } from "@shared/components";
import FileCard from "@shared/components/fileCard/FileCard";
import { useBeforeUnload } from "@shared/hooks";
import {
  ApplicantsExamsFiles,
  useApplicantsExamsFileList,
  useGlobalFileUploadConfig,
  useRemoveApplicantsExamFileList,
} from "@shared/hooks/query";
import {
  FileList,
  FileUploadConfig,
  FileUploadStatus,
} from "@shared/services/fileUpload";
import history from "@shared/services/history";
import Message from "@shared/services/message";
import { formatBytes } from "@shared/services/string";

import { useApplicantFileUpload } from "../editorAppealComment/useApplicantFileUpload";

interface EditorFileUploaderProps extends RouteComponentProps {
  challengeResultId: number;
  fileUploadConfig: FileUploadConfig;
  updateFileUploadingState: (isUploading: boolean) => void;
}

const EditorFileUploader = ({
  challengeResultId,
  fileUploadConfig,
  location,
  updateFileUploadingState,
}: EditorFileUploaderProps) => {
  const isUploadModalOpen = location.hash === "#upload";
  const { applicantExamId } = useStoreContext();
  const {
    files: filesToUpload,
    isLoading,
    uploadFiles,
    removeFileUploads,
  } = useApplicantFileUpload({
    applicantExamId,
    resultId: challengeResultId,
  });
  const { data: cloudFileList } = useApplicantsExamsFileList(challengeResultId);
  const removeFile = useRemoveApplicantsExamFileList();
  const [isDirty, setIsDirty] = React.useState(false);
  const [initiateUpload, setInitiateUpload] = React.useState(false);
  const [uploadingFiles, setUploadingFiles] = React.useState<FileList[]>([]);

  const label = {
    title: Message.getMessageByKey("appeal.fileUploader"),
    description: Message.getMessageByKey("appeal.fileUploader.description"),
    uploadCta: Message.getMessageByKey("button.uploadFileModal"),
    empty: Message.getMessageByKey("appeal.fileUploader.empty"),
  };
  const { data: globalFileUploadConfig } = useGlobalFileUploadConfig();

  const maxUploadLimit = globalFileUploadConfig.maxUploadCountLimit;
  const newUploadFileList = React.useMemo(
    () =>
      filesToUpload.map<ApplicantsExamsFiles>(({ id, file, status }) => {
        const isError = status === FileUploadStatus.FAILED;
        const tooltip = Message.getMessageByKey(
          [FileUploadStatus.FAILED, FileUploadStatus.CANCELLED].includes(status)
            ? "retry"
            : "delete",
        );

        return {
          id,
          file,
          filename: file.name,
          displaySize: formatBytes(file.size),
          cardStatus: {
            isError,
            isRemove: status === FileUploadStatus.COMPLETE,
            isRetry: [
              FileUploadStatus.CANCELLED,
              FileUploadStatus.FAILED,
            ].includes(status),
            isUploading: [
              FileUploadStatus.NOT_STARTED,
              FileUploadStatus.UPLOADING,
            ].includes(status),
          },
          sizeInBytes: file.size,
          status,
          statusMessage: isError
            ? Message.getMessageByKey("fileUploader.uploading.failed")
            : undefined,
          tooltip,
        };
      }),
    [filesToUpload],
  );
  const uploadFileList = Array.from(
    new Map<string, ApplicantsExamsFiles>(
      [...cloudFileList, ...newUploadFileList].map((item) => [
        item.filename,
        item,
      ]),
    ).values(),
  );
  const initialFileList = uploadFileList.map(({ filename, sizeInBytes }) => ({
    filename,
    sizeInBytes,
  }));
  const isEmpty = uploadFileList.length < 1;
  const isPrompt = (isDirty && !initiateUpload) || isLoading;

  const handleDisplayModal = (value: boolean) => () => {
    setIsDirty(false);
    history.replace({ hash: value ? "upload" : "" });
  };

  const handleUpload = async (files: FileList[]) => {
    setUploadingFiles(files);
    setInitiateUpload(true);
  };

  const handleClickFileCard =
    ({
      file,
      filename,
      status,
    }: {
      file?: File;
      filename: string;
      status: FileUploadStatus;
    }) =>
    async () => {
      switch (status) {
        case FileUploadStatus.COMPLETE:
          try {
            await removeFile.mutateAsync({
              applicantExamId,
              resultId: challengeResultId,
              data: { filenames: [filename] },
            });
            removeFileUploads([filename]);
          } catch (_) {}
          break;
        case FileUploadStatus.FAILED:
          if (file) {
            uploadFiles([file]);
          }
          break;
        case FileUploadStatus.CANCELLED:
          removeFileUploads([filename]);
          break;
        default:
          break;
      }
    };

  const handleRemoveFile = (name: string) => () => {
    removeFileUploads([name]);
  };

  const handleDirty = (value: boolean) => {
    setIsDirty(value);
  };

  React.useEffect(() => {
    if (!initiateUpload) {
      return;
    }

    handleDisplayModal(false)();
    uploadFiles(uploadingFiles.map(({ file }) => file));
    setInitiateUpload(false);
  }, [initiateUpload, uploadFiles, uploadingFiles]);

  React.useEffect(() => {
    updateFileUploadingState(isLoading);
  }, [isLoading, updateFileUploadingState]);

  useBeforeUnload(
    React.useCallback(
      (event: BeforeUnloadEvent) => {
        if (!isPrompt) {
          return;
        }

        const message = "navigation.confirm";

        if (event) {
          event.returnValue = message;
        }

        return message;
      },
      [isPrompt],
    ),
  );

  return (
    <div className="editor-file-uploader">
      <Prompt
        when={isPrompt}
        message={Message.getMessageByKey("navigation.confirm")}
      />
      <div className="editor-file-uploader__content">
        <div className="editor-file-uploader__header">
          <div className="editor-file-uploader__title is-text-ellipsis">
            {label.title}
          </div>
          <Button
            className="text-lg"
            type="secondary"
            onClick={handleDisplayModal(true)}
            disabled={!fileUploadConfig.uploadEnabled || isLoading}
          >
            {label.uploadCta}
          </Button>
        </div>
        <p className="text-xs">{label.description}</p>
        {isEmpty ? (
          <div className="editor-file-uploader__empty">
            <Icon type="file" size="5xl" color="neutral-200" />
            <p className="editor-file-uploader__empty-text">{label.empty}</p>
          </div>
        ) : (
          <ul className="editor-file-uploader__list">
            {uploadFileList.map(
              ({
                file,
                filename,
                displaySize,
                cardStatus,
                status,
                statusMessage,
                tooltip,
              }) => (
                <li key={filename}>
                  <FileCard
                    fileName={filename}
                    fileSize={displaySize}
                    {...cardStatus}
                    statusMessage={statusMessage}
                    actionTooltip={tooltip}
                    onClick={handleClickFileCard({ file, filename, status })}
                    onClose={
                      status !== FileUploadStatus.COMPLETE
                        ? handleRemoveFile(filename)
                        : undefined
                    }
                  />
                </li>
              ),
            )}
          </ul>
        )}
      </div>
      {isUploadModalOpen && (
        <FileUploadModal
          initialFileList={initialFileList}
          initialRemainingSize={globalFileUploadConfig.maxUploadSizeBytes}
          initialRemainingLimit={maxUploadLimit}
          onUpload={handleUpload}
          onDirty={handleDirty}
          onClickCancel={handleDisplayModal(false)}
        />
      )}
    </div>
  );
};

export default withRouter(EditorFileUploader);
